import styled, { css } from 'styled-components';

import { COLORS, HOME, ANIM } from '../../theme/tokens';
import { Text } from '../Text';

/** Full-width measured host — the SVG sizes itself to this element's width.
 *  `position: relative` anchors the absolutely-positioned hover tooltip. */
export const Wrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

export const Svg = styled.svg`
  display: block;
  max-width: 100%;
  /* The hover crosshair targets discrete data points, so a crosshair cursor
     reads truer than the default arrow. */
  cursor: crosshair;
  /* Touch: let a vertical drag scroll the page through the chart, but keep the
     horizontal axis for us so a finger-slide scrubs the readout between points
     instead of being eaten by the browser. */
  touch-action: pan-y;
  /* No grey flash / text-selection when tapping or holding to scrub on mobile. */
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  user-select: none;
`;

/**
 * Floating hover readout — a dark card listing every series' value at the
 * hovered point, colour-coded to the legend. Positioned (left/right + top/bottom)
 * inline by the component against the crosshair; never intercepts the pointer so
 * it can't flicker or block an underlying card click.
 *
 * `$on` drives the enter/exit choreography: the card fades + rises in as the
 * pointer enters the plot and fades + drops out (a touch quicker) as it leaves.
 * The component keeps it mounted through the exit so the transition can play
 * (the area's keep-mounted-then-drop pattern); the duration reads from the
 * target state, so entering uses ENTER_MS and leaving uses the shorter EXIT_MS.
 * The translateY here composes with the inline top/bottom/left/right placement.
 */
export const Tooltip = styled.div<{ $on: boolean }>`
  position: absolute;
  z-index: 5;
  pointer-events: none;
  min-width: 132px;
  padding: 9px 11px;
  border-radius: 11px;
  background: ${HOME.menuBg};
  border: 1px solid ${COLORS.panelBorder};
  box-shadow: ${HOME.menuShadow};
  backdrop-filter: blur(6px);
  opacity: ${(p) => (p.$on ? 1 : 0)};
  transform: translateY(${(p) => (p.$on ? '0' : `${ANIM.CHART_TIP_RISE_PX}px`)});
  transition:
    opacity ${(p) => (p.$on ? ANIM.CHART_TIP_ENTER_MS : ANIM.CHART_TIP_EXIT_MS)}ms ${ANIM.EASE},
    transform ${(p) => (p.$on ? ANIM.CHART_TIP_ENTER_MS : ANIM.CHART_TIP_EXIT_MS)}ms ${ANIM.EASE};
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The hovered x-axis label (e.g. "Wed") — the tooltip's heading. */
export const TipDay = styled(Text)`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${HOME.t400};
  margin-bottom: 7px;
  white-space: nowrap;
`;

export const TipRows = styled.div`
  display: grid;
  gap: 6px;
`;

export const TipRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px;
`;

/** Series swatch — a solid dot, or a hollow dashed ring for the dashed series,
 *  mirroring the chart legend. Hue is a token value handed in by the caller. */
export const TipSwatch = styled.i<{ $color: string; $dashed: boolean }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: none;
  ${(p) =>
    p.$dashed
      ? css`
          background: transparent;
          border: 1.5px dashed ${p.$color};
        `
      : css`
          background: ${p.$color};
          /* 0x26 is about 15% alpha. The colour is now a var(--ows-...) token, so the
             old hex-append (colour + '26') produced invalid CSS and the glow ring
             silently dropped. color-mix keeps the soft ring under CSS-var theming. */
          box-shadow: 0 0 0 3px color-mix(in srgb, ${p.$color} 15%, transparent);
        `}
`;

export const TipName = styled(Text)`
  font-size: 12px;
  font-weight: 400;
  color: ${HOME.t300};
  white-space: nowrap;
`;

export const TipValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textStrong};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
